import React, {FormEvent, useEffect, useState} from 'react';
import {Checkbox, DefaultButton, Panel, MessageBar, MessageBarType} from "@fluentui/react";
import {t} from "i18next";
import {getSearchFacetsApi} from '../api';
import {FilterRegular} from "@fluentui/react-icons";
import {displayFacetValue} from '../utils/siteNames';



/**
 * Typ für die einzelnen Filteroptionen.
 */
export interface FilterOption {
    label: string;
    options: {
        count: number;
        value: string;
        label: string;
    }[];
}

interface FilterOptionItemProps {
    filterOption: FilterOption;
    searchTerms: Record<string, string>;
    selectedFilters: Record<string, string[]>;
    onFilterChange: (filterCategory: string, value: string, checked: boolean | undefined) => void;
    setSearchTerms: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    fetchWithFacetFilter: (facetName: string, prefix: string) => void;
}

function FilterOptionItem({ 
    filterOption, 
    searchTerms, 
    selectedFilters, 
    onFilterChange,
    setSearchTerms,
    fetchWithFacetFilter
}: FilterOptionItemProps) {
    // Use the exact facet name as it comes from the backend for API calls
    const facetKey = filterOption.label;
    const searchTerm = searchTerms[facetKey] ?? "";
    
    // Track loading state
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Local filtering is only used for immediate visual feedback while the API call is in progress.
    // Match against both the raw value/label and the friendly display label so typing the friendly
    // site name (e.g. "Detau" / "Life Time Services") still finds the option.
    const filteredOptions = filterOption.options.filter(opt => {
        const term = searchTerm.toLowerCase();
        return (
            opt.label.toLowerCase().includes(term) ||
            displayFacetValue(facetKey, opt.value).toLowerCase().includes(term)
        );
    });

    // Ensure selected values are always visible, even if not in current backend response
    const selectedValues = selectedFilters?.[facetKey] ?? [];
    const ensureSelectedVisible = (options: typeof filteredOptions) => {
        const optionValues = new Set(options.map(opt => opt.value));
        const missingSelected = selectedValues.filter(selectedValue => !optionValues.has(selectedValue));
        
        // Add missing selected values with 0 count
        const additionalOptions = missingSelected.map(value => ({
            value,
            label: value,
            count: 0
        }));
        
        return [...options, ...additionalOptions];
    };

    // Apply the function to ensure selected values are visible
    const finalFilteredOptions = ensureSelectedVisible(filteredOptions);

    // Handle search input changes with live prefix search
    const handleSearchChange = (newValue: string) => {        
        // Always update the local state immediately for responsive UI
        setSearchTerms(prev => ({
            ...prev,
            [facetKey]: newValue
        }));

        // Show loading indicator
        setIsLoading(true);
        
        // Use debounced API call for all cases to prevent race conditions
        fetchWithFacetFilter(facetKey, newValue);
    };
    
    // Update loading state when options change
    useEffect(() => {
        setIsLoading(false);
    }, [filterOption.options]);

    return (
        <div className={"border rounded mb-2 overflow-hidden"}>
            <div className={"bg-stone-50 p-2 font-semibold"}>
                {t(`labels.searchFilter.${facetKey}`) ?? facetKey}
            </div>

            <div className="px-3 py-2 relative">
                <input
                    type="text"
                    className="w-full p-1 border rounded"
                    placeholder={t("labels.search")}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
                {isLoading && (
                    <div className="absolute right-5 top-1/2 transform -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                )}
            </div>

            <div className={"p-3 space-y-1 h-32 overflow-y-auto"}>
                {finalFilteredOptions.length > 0 ? (
                    finalFilteredOptions.map(({ value, label, count }, idx) => {
                        const isChecked = (selectedFilters?.[facetKey] ?? []).includes(value);
                        return (
                            <Checkbox
                                label={`${displayFacetValue(facetKey, value)} (${count}x)`}
                                onChange={(ev, checked) => onFilterChange(facetKey, value, checked)}
                                key={`${idx}`}
                                checked={isChecked}
                            />
                        );
                    })
                ) : (
                    searchTerm.trim() !== "" && (
                        <div className="text-gray-500 text-sm italic">
                            {t("labels.noMatchingResults") || "No matching results"}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

/**
 * Eine Komponente, die eine Benutzeroberfläche für Filteroptionen bereitstellt.
 * Enthält eine Umschaltfunktionalität für die Anzeige eines Filter-Panels und Checkboxen für die Filterauswahl.
 *
 * @return {JSX.Element} Die JSX-Struktur, die die Suchfilterkomponente repräsentiert, einschließlich einem anklickbaren Filter-Schalter und einem Panel mit auswählbaren Filteroptionen.
 */
function SearchFilter({
                        selectedFilters,
                        setSelectedFilters,
                        hideButton = false
                    }: {
    selectedFilters: Record<string, string[]>,
    setSelectedFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>,
    hideButton?: boolean
}): JSX.Element {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
    const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
    const [notFoundMessage, setNotFoundMessage] = useState<string | null>(null);
    // Add debounce to prevent too many API calls
    const [facetSearchTimeout, setFacetSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

    const fetchSearchFacets = async (facetFilter?: { facetName: string, prefix: string }) => {
        try {
            const facetsResponse = await getSearchFacetsApi(selectedFilters, "", facetFilter);
            
            if (facetsResponse.status === 'success' && facetsResponse.facets) {
                // Log the raw facets data from the API
               
                let dynamicFilterOptions: FilterOption[] = Object.entries(facetsResponse.facets).map(([key, values]) => ({
                    label: key, // Use exact key as received from backend
                    options: values as { count: number; value: string; label: string }[]
                }));
                
                // Log the labels                
                dynamicFilterOptions = dynamicFilterOptions.filter(filterOption => filterOption.options.length > 0);
                
                // Create a manually ordered array to strictly enforce the desired order
                // Use exact field names as they come from backend
                const orderedLabels = ["documenttype", "Product", "machinenumber", "sitename", "documentformat", "sourcesystem"];
                
                // Log facets before sorting                
                // Sort the filter options according to the strict order defined above
                dynamicFilterOptions.sort((a, b) => {
                    const aIdx = orderedLabels.indexOf(a.label);
                    const bIdx = orderedLabels.indexOf(b.label);
                    
                    // If both items are in our ordered list, sort by their position in that list
                    if (aIdx !== -1 && bIdx !== -1) {
                        return aIdx - bIdx;
                    }
                    
                    // If only a is in the list, it comes first
                    if (aIdx !== -1) return -1;
                    
                    // If only b is in the list, it comes first
                    if (bIdx !== -1) return 1;
                    
                    // If neither is in the list, sort alphabetically
                    return a.label.localeCompare(b.label);
                });

               
                
                // If we're filtering a specific facet, only update that one
                if (facetFilter) {
                    setFilterOptions(prevOptions => {
                        // Find the specific facet in the current options
                        const updatedOptions = [...prevOptions];
                        const facetIndex = updatedOptions.findIndex(
                            opt => opt.label === facetFilter.facetName
                        );
                        
                        if (facetIndex !== -1) {
                            // Find the same facet in the new options
                            const newFacet = dynamicFilterOptions.find(
                                opt => opt.label === facetFilter.facetName
                            );
                            
                            if (newFacet) {
                                updatedOptions[facetIndex] = newFacet;
                            }
                        }
                        
                        return updatedOptions;
                    });
                } else {
                    // If no facet filter, update all options
                    setFilterOptions(dynamicFilterOptions);
                }
                
                setNotFoundMessage(null);
            } else if (facetsResponse.status === 'error' && facetsResponse.facets) {
                let dynamicFilterOptions: FilterOption[] = Object.entries(facetsResponse.facets).map(([key, values]) => ({
                    label: key, // Use exact key as received from backend
                    options: values as { count: number; value: string; label: string }[]
                }));
                dynamicFilterOptions = dynamicFilterOptions.filter(filterOption => filterOption.options.length > 0);
                
                // Create a manually ordered array to strictly enforce the desired order
                // Use exact field names as they come from backend
                const orderedLabels = ["documenttype", "Product", "machinenumber", "sitename", "documentformat", "sourcesystem"];
                
                
                // Sort the filter options according to the strict order defined above
                dynamicFilterOptions.sort((a, b) => {
                    const aIdx = orderedLabels.indexOf(a.label);
                    const bIdx = orderedLabels.indexOf(b.label);
                    
                    // If both items are in our ordered list, sort by their position in that list
                    if (aIdx !== -1 && bIdx !== -1) {
                        return aIdx - bIdx;
                    }
                    
                    // If only a is in the list, it comes first
                    if (aIdx !== -1) return -1;
                    
                    // If only b is in the list, it comes first
                    if (bIdx !== -1) return 1;
                    
                    // If neither is in the list, sort alphabetically
                    return a.label.localeCompare(b.label);
                });

               
                setFilterOptions(dynamicFilterOptions);
                setNotFoundMessage(facetsResponse.message || t('labels.filterValueNotFound'));
            } else {
                setNotFoundMessage(null);
            }
        } catch (error) {
            console.error("Failed to fetch search facets:", error);
            setNotFoundMessage(null);
        }
    };
    
    // Function to be passed to the FilterOptionItem components
    const fetchWithFacetFilter = (facetName: string, prefix: string) => {
        // Clear any existing timeout
        if (facetSearchTimeout) {
            clearTimeout(facetSearchTimeout);
        }
        
        // For empty strings, execute immediately to provide instant feedback
        if (prefix.length === 0) {
            fetchSearchFacets({ facetName: facetName, prefix });
            return;
        }
        
        // Set a new timeout to debounce the API calls for non-empty prefixes
        const timeout = setTimeout(() => {
            fetchSearchFacets({ facetName: facetName, prefix });
        }, 75); // Ultra-short debounce time for faster response
        
        setFacetSearchTimeout(timeout);
    };
    
    useEffect(() => {
        fetchSearchFacets();
    }, []);
    
    useEffect(() => {
        // If any category has an active search term, send it as a facet filter directly
        const activeSearchEntries = Object.entries(searchTerms).filter(([_, term]) => term.trim().length > 0);

        if (activeSearchEntries.length > 0) {
            activeSearchEntries.forEach(([facetName, term]) => {
                fetchSearchFacets({ facetName, prefix: term });
            });
        } else {
            fetchSearchFacets();
        }
    }, [selectedFilters]);
    
    // Clean up timeout on component unmount
    useEffect(() => {
        return () => {
            if (facetSearchTimeout) {
                clearTimeout(facetSearchTimeout);
            }
        };
    }, [facetSearchTimeout]);
    
    const _onChange = (
        filterCategory: string,
        value: string,
        checked: boolean | undefined
    ): void => {
        // Use the exact category name as it comes from the backend
        setSelectedFilters(prev => {
            const currentFilters = prev[filterCategory] ?? [];
            let newFilters = [];

            if (checked) {
                newFilters = [...new Set([...currentFilters, value])];
            } else {
                newFilters = currentFilters.filter(f => f !== value);
            }

            return {
                ...prev,
                [filterCategory]: newFilters
            };
        });
    };

    let isFilterActive = Object.keys(selectedFilters).length > 0;

    // If hideButton is true, render only the filter content without button/panel
    if (hideButton) {
        return (
            <div className={"mt-2"}>
                {notFoundMessage && (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline={false} className="mb-3">
                        {notFoundMessage}
                    </MessageBar>
                )}
                {filterOptions.map((filterOption, index) => (
                    <FilterOptionItem
                        key={index}
                        filterOption={filterOption}
                        searchTerms={searchTerms}
                        selectedFilters={selectedFilters}
                        onFilterChange={_onChange}
                        setSearchTerms={setSearchTerms}
                        fetchWithFacetFilter={fetchWithFacetFilter}
                    />
                ))}
            </div>
        );
    }

    return (
        <div>
            <div
                className={`h-10 w-fit cursor-pointer border p-2 rounded bg-white flex items-center justify-center ${isFilterActive && ("ring-1")}`}
                onClick={() => setIsOpen(!isOpen)}>
                <FilterRegular/>
                <p className={"ml-1"}>{t("labels.filter")}</p>
            </div>
            <Panel
                headerText={t("labels.filter")}
                isOpen={isOpen}
                isBlocking={false}
                onDismiss={() => setIsOpen(false)}
                closeButtonAriaLabel={t("labels.closeButton")}
                onRenderFooterContent={() => (
                    <DefaultButton onClick={() => setIsOpen(false)}>
                        {t("labels.closeButton")}
                    </DefaultButton>
                )}
                isFooterAtBottom={true}
            >
                <div className={"mt-2"}>
                    {notFoundMessage && (
                        <MessageBar messageBarType={MessageBarType.warning} isMultiline={false} className="mb-3">
                            {notFoundMessage}
                        </MessageBar>
                    )}
                    {filterOptions.map((filterOption, index) => (
                        <FilterOptionItem
                            key={index}
                            filterOption={filterOption}
                            searchTerms={searchTerms}
                            selectedFilters={selectedFilters}
                            onFilterChange={_onChange}
                            setSearchTerms={setSearchTerms}
                            fetchWithFacetFilter={fetchWithFacetFilter}
                        />
                    ))}
                </div>
            </Panel>
        </div>
    );
}

export default SearchFilter;